/**
 * Trinkspiele im Zonenchat (amendment 20): Wabenwürfel, Heiß oder kalt, Stundenwette and
 * Waben-Bingo — the games for a whole round rather than for two.
 *
 * They sit in a district's chat like the games of amendment 19 and keep its three rules: the
 * server holds the game, nothing waits for ever, and the dice are the server's. What is new is
 * that anybody in the chat can take part. So each player's stake — a vote, a wager, a bingo
 * card — is a row of its own beside the game, and the outcome is worked out from those rows and
 * the clock whenever somebody looks.
 *
 * A Schluck may always be water: the games say how many, never of what.
 *
 * Pure like the rest of the directory: the rules, the tallies, the card and its marks. The
 * clock, the dice and what happened in the district come from the caller.
 */

import { K } from './gdi.ts';

export const PARTY_GAMES = ['dice', 'hotcold', 'bet', 'bingo'] as const;
export type PartyGame = (typeof PARTY_GAMES)[number];

export function isPartyGame(value: unknown): value is PartyGame {
  return PARTY_GAMES.some((game) => game === value);
}

export const PARTY_NAMES: Record<PartyGame, string> = {
  dice: 'Wabenwürfel',
  hotcold: 'Heiß oder kalt',
  bet: 'Stundenwette',
  bingo: 'Waben-Bingo',
};

/**
 * What a round says as a message: the line a push shows, and what an app from before these
 * games draws in their place. A roll of the die says what it rolled (`rollLine`).
 */
export const PARTY_INVITATIONS: Record<Exclude<PartyGame, 'dice'>, string> = {
  hotcold: '🔥🧊 Heiß oder kalt? Stimmt ab – die Minderheit trinkt.',
  bet: '⏳ Stundenwette: Wird die heißeste Wabe im Viertel heißer oder kälter?',
  bingo: '🎱 Waben-Bingo: Schreibt auf, was heute passiert – bestätigen es zwei, trinken alle.',
};

// ---------------------------------------------------------------------------------------
// Wabenwürfel
// ---------------------------------------------------------------------------------------

/**
 * What each face asks. A hexagon has six neighbours and a die six faces, and the six is the
 * face that sends the round on to the next cell.
 */
export const DICE_FACES = [
  'Du trinkst einen Schluck.',
  'Verteil zwei Schlucke.',
  'Alle trinken einen Schluck.',
  'Wasserfall – du fängst an.',
  'Stell eine neue Regel auf.',
  'Wabensprung! Ab in die nächste heiße Wabe – wer zuletzt ankommt, trinkt.',
] as const;

export type Dice = { kind: 'dice'; face: number };

/** The roll for a draw in `0 … 5`, made by the caller. */
export function rollFor(draw: number): Dice {
  const valid = Number.isInteger(draw) && draw >= 0 && draw < DICE_FACES.length;
  return { kind: 'dice', face: valid ? draw + 1 : 1 };
}

export function taskOf(face: number): string {
  return DICE_FACES[face - 1] ?? DICE_FACES[0];
}

/** A roll as a message: what a push and an older app show. */
export function rollLine(dice: Dice): string {
  return `🎲 ${dice.face} – ${taskOf(dice.face)}`;
}

export function readDice(value: unknown): Dice | null {
  if (typeof value !== 'object' || value === null) return null;
  const { kind, face } = value as Record<string, unknown>;
  const valid = typeof face === 'number' && Number.isInteger(face) && face >= 1 && face <= 6;
  return kind === 'dice' && valid ? { kind, face } : null;
}

// ---------------------------------------------------------------------------------------
// Heiß oder kalt
// ---------------------------------------------------------------------------------------

/**
 * How long a round takes votes: long enough to get a phone out of a pocket after "stimmt ab!",
 * short enough to still be a round. Whoever asked can close it sooner.
 */
export const VOTE_MS = 90 * 1000;

export const VOTES = ['hot', 'cold'] as const;
export type Vote = (typeof VOTES)[number];

export function isVote(value: unknown): value is Vote {
  return VOTES.some((vote) => vote === value);
}

/**
 * The round. It names nothing: whom or what the round has just met, the round knows, and a
 * description of a stranger has no place in a chat the whole district reads. The votes are
 * entries beside it; `closedAt` is set when whoever asked closed it early.
 */
export type HotCold = { kind: 'hotcold'; closedAt?: number };

export function readHotCold(value: unknown): HotCold | null {
  if (typeof value !== 'object' || value === null) return null;
  const { kind, closedAt } = value as Record<string, unknown>;
  if (kind !== 'hotcold') return null;
  if (closedAt === undefined) return { kind };
  return typeof closedAt === 'number' ? { kind, closedAt } : null;
}

/** When the round stops taking votes: at its deadline, or earlier if it was closed. */
export function votingEndsAt(state: HotCold, createdAt: number): number {
  const deadline = createdAt + VOTE_MS;
  return state.closedAt !== undefined && state.closedAt < deadline ? state.closedAt : deadline;
}

export type Tally = {
  hot: number;
  cold: number;
  /** The sides that drink: the smaller one, both on a tie, none without a minority. */
  drinks: Vote[];
};

/**
 * The rule of the round: the minority drinks. A tie is everybody's doing, so everybody drinks;
 * a single vote, or every vote the same, leaves nobody in a minority.
 */
export function tallyOf(votes: readonly Vote[]): Tally {
  const hot = votes.filter((vote) => vote === 'hot').length;
  const cold = votes.length - hot;
  if (hot === 0 || cold === 0) return { hot, cold, drinks: [] };
  if (hot === cold) return { hot, cold, drinks: ['hot', 'cold'] };
  return { hot, cold, drinks: [hot < cold ? 'hot' : 'cold'] };
}

// ---------------------------------------------------------------------------------------
// Stundenwette
// ---------------------------------------------------------------------------------------

/** A wager is on how the cell stands an hour after it was offered. */
export const BET_MS = 60 * 60 * 1000;

/** Wagers are taken this long. Later, the hour has already half told its story. */
export const BET_WINDOW_MS = 10 * 60 * 1000;

export const WAGERS = ['hotter', 'colder'] as const;
export type Wager = (typeof WAGERS)[number];

export function isWager(value: unknown): value is Wager {
  return WAGERS.some((wager) => wager === value);
}

/**
 * The cell, and how hot it was when the wager was offered and an hour later — as `raw`, the
 * unrounded sum behind the GDI. An hour of decay moves the rounded GDI by well under one, so on
 * the rounded number a draw would be the usual outcome rather than the exception.
 */
export type Bet = { kind: 'bet'; cell: string; start: number; end?: number };

export function readBet(value: unknown): Bet | null {
  if (typeof value !== 'object' || value === null) return null;
  const { kind, cell, start, end } = value as Record<string, unknown>;
  if (kind !== 'bet' || typeof cell !== 'string' || typeof start !== 'number') return null;
  if (end === undefined) return { kind, cell, start };
  return typeof end === 'number' ? { kind, cell, start, end } : null;
}

/** The GDI to one decimal, so that a difference the rounded number hides can be shown. */
export function preciseGdi(raw: number): number {
  return raw <= 0 ? 0 : Math.round(1000 * (1 - Math.exp(-raw / K))) / 10;
}

/**
 * How the hour went. Hotter means more is going on than an hour before. An hour without a new
 * report always ends colder, because left alone a GDI only ever decays.
 */
export function verdictOf(start: number, end: number): Wager {
  return end > start ? 'hotter' : 'colder';
}

export type BetStatus = 'open' | 'running' | 'over' | 'expired';

export function betStatus(clock: { now: number; createdAt: number; wagers: number }): BetStatus {
  if (clock.now - clock.createdAt < BET_WINDOW_MS) return 'open';
  if (clock.wagers === 0) return 'expired';
  return clock.now - clock.createdAt < BET_MS ? 'running' : 'over';
}

// ---------------------------------------------------------------------------------------
// Waben-Bingo
// ---------------------------------------------------------------------------------------

/**
 * The fields are the players' own: everybody who plays writes up to two things that might
 * happen tonight, and the board is all of them, as a honeycomb around a beer. Ten players make
 * twenty fields.
 *
 * Nobody marks a field alone. Once two different players say it happened, it did — one is
 * wishful thinking, two is a witness — and the whole round drinks.
 */
export const FIELDS_PER_PLAYER = 2;

/** Confirmations from this many different players make a field count. */
export const CONFIRMATIONS = 2;

/** What a field may say: one line, short enough to fit a hexagon on a phone. */
export const FIELD_MAX = 40;

/** An evening is long, but a bingo still ends three hours after it began. */
export const BINGO_MS = 3 * 60 * 60 * 1000;

/**
 * A honeycomb is full from this many fields on, every one of them counted — two players'
 * worth. Fewer is not a round yet, however many of them happened: two quick confirmations
 * must not end a game the others have not even written a field for.
 */
export const FULL_MIN = 4;

/** The game itself; its fields, and who confirmed them, are rows of their own. */
export type Bingo = { kind: 'bingo' };

export function readBingo(value: unknown): Bingo | null {
  if (typeof value !== 'object' || value === null) return null;
  return (value as Record<string, unknown>).kind === 'bingo' ? { kind: 'bingo' } : null;
}

export type FieldCheck = { ok: true; value: string } | { ok: false };

/**
 * A field's text: one line, not empty, at most `FIELD_MAX` characters, counted as somebody
 * typing counts them. Control characters and line breaks go; runs of spaces become one.
 */
export function checkField(text: unknown): FieldCheck {
  if (typeof text !== 'string') return { ok: false };
  const line = [...text]
    .map((char) => ((char.codePointAt(0) ?? 0) < 0x20 || char === '\u007f' ? ' ' : char))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
  if (line === '' || [...line].length > FIELD_MAX) return { ok: false };
  return { ok: true, value: line };
}

/** When a field counted: at its second confirmation. Null until then. */
export function markedAt(confirmedAt: readonly number[]): number | null {
  if (confirmedAt.length < CONFIRMATIONS) return null;
  return [...confirmedAt].sort((a, b) => a - b)[CONFIRMATIONS - 1] ?? null;
}

/**
 * Where the round stands: over once every field has counted and there are at least
 * `FULL_MIN` of them — the full honeycomb — or three hours after it began.
 */
export function bingoState(
  fields: readonly { markedAt: number | null }[],
  clock: { now: number; createdAt: number },
): { over: boolean; endsAt: number; full: boolean } {
  const deadline = clock.createdAt + BINGO_MS;
  let last: number | null = fields.length < FULL_MIN ? null : 0;
  for (const field of fields) {
    if (field.markedAt === null || last === null) {
      last = null;
      break;
    }
    last = Math.max(last, field.markedAt);
  }
  const full = last !== null && last < deadline;
  const endsAt = full && last !== null ? last : deadline;
  return { over: clock.now >= endsAt, endsAt, full };
}

// ---------------------------------------------------------------------------------------
// What a reader is sent
// ---------------------------------------------------------------------------------------

export type DiceView = { kind: 'dice'; face: number; task: string };

export type HotColdView = {
  kind: 'hotcold';
  status: 'open' | 'over';
  /** When voting ends or ended, in milliseconds since the epoch. */
  endsAt: number;
  /** How many have voted: the number alone, until it is over. */
  votes: number;
  mine: Vote | null;
  /** Whether the reader asked, and so may close the round early. */
  asked: boolean;
  result: (Tally & { drinkers: string[] }) | null;
};

export type BetView = {
  kind: 'bet';
  status: BetStatus;
  cell: string;
  /** The GDI to one decimal when the wager was offered, and an hour later. */
  start: number;
  end: number | null;
  /** When wagers close, and when the hour is up, in milliseconds since the epoch. */
  closesAt: number;
  endsAt: number;
  wagers: { username: string; wager: Wager }[];
  mine: Wager | null;
  verdict: Wager | null;
  /** Who bet the other way, once it is decided. */
  drinkers: string[];
};

export type BingoField = {
  id: string;
  text: string;
  author: string;
  mine: boolean;
  /** How many players have confirmed it, and whether the reader is one of them. */
  confirmations: number;
  confirmed: boolean;
  /** When it counted, in milliseconds since the epoch: null until two have confirmed it. */
  markedAt: number | null;
};

export type BingoView = {
  kind: 'bingo';
  status: 'running' | 'over';
  endsAt: number;
  /** Over because every field counted, rather than because the evening ran out. */
  full: boolean;
  fields: BingoField[];
  /** How many fields the reader may still write. */
  free: number;
  /** Whether the reader plays — has a field on the board, and so may confirm. */
  playing: boolean;
  players: number;
};

export type PartyView = DiceView | HotColdView | BetView | BingoView;
